import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ProtectedCustomerRoute } from '@/components/auth/ProtectedCustomerRoute';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';
import { PublicUserRegistrationPage } from '@/pages/PublicUserRegistrationPage';
import { VolunteerRegistrationPage } from '@/pages/VolunteerRegistrationPage';
import { VideoCallPage } from '@/pages/VideoCallPage';
import { PublicClinicPage } from '@/pages/PublicClinicPage';
import { QuestionnaireLinkPage } from '@/pages/QuestionnaireLinkPage';
import { GroupInvitePage } from '@/pages/GroupInvitePage';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AdminDashboard } from '@/pages/dashboard/AdminDashboard';
import { CoachDashboard } from '@/pages/dashboard/CoachDashboard';
import { CustomerDashboard } from '@/pages/dashboard/CustomerDashboard';
import { ExaminerDashboard } from '@/pages/dashboard/ExaminerDashboard';
import { ManagerDashboard } from '@/pages/dashboard/ManagerDashboard';
import { PartnerDashboard } from '@/pages/dashboard/PartnerDashboard';
import { VolunteerDashboard } from '@/pages/dashboard/VolunteerDashboard';
import { HabitsPage } from '@/pages/features/HabitsPage';
import { SupportPage } from '@/pages/features/SupportPage';
import { CoachingPage } from '@/pages/features/CoachingPage';
import { EducationPage } from '@/pages/features/EducationPage';
import { PointsPage } from '@/pages/features/PointsPage';
import { SettingsPage } from '@/pages/features/SettingsPage';
import { TripsPage } from '@/pages/features/TripsPage';
import { TreatmentSupportPage } from '@/pages/features/TreatmentSupportPage';
import { InstructorDashboard } from '@/pages/dashboard/InstructorDashboard';
import { OrganizerDashboard } from '@/components/dashboard/OrganizerDashboard';
import { ProviderDashboard } from '@/components/dashboard/ProviderDashboard';
import { CoordinatorDashboard } from '@/components/dashboard/CoordinatorDashboard';
import { MarketerDashboard } from '@/components/dashboard/MarketerDashboard';
import { AuditDashboard } from '@/pages/dashboard/AuditDashboard';
import { LandingPage } from '@/pages/landing/LandingPage';
import { LandingArticleDetailPage } from '@/pages/landing/LandingArticleDetailPage';
import { LandingPage as DynamicLandingPage } from '@/pages/LandingPage';
import { DeleteAccountPage } from '@/pages/DeleteAccountPage';
import { RequestTreatmentPage } from '@/pages/RequestTreatmentPage';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <AuthProvider navigate={navigate} location={location}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/new/:organizationId" element={<PublicUserRegistrationPage />} />
          <Route path="/video-call/:linkId" element={<VideoCallPage />} />
          <Route path="/video-call/customer/:linkId" element={<VideoCallPage />} />
          <Route path="/clinic/:coachId" element={<PublicClinicPage />} />
          <Route path="/clinic/:coachId/:linkId" element={<PublicClinicPage />} />
          {/* Public questionnaire link (#125) */}
          <Route path="/q" element={<QuestionnaireLinkPage />} />
          <Route path="/group/:groupId" element={<GroupInvitePage />} />
          
          {/* Delete Account - Public (Google Play requirement) */}
          <Route path="/delete-account" element={<DeleteAccountPage />} />

          {/* Landing Page Route */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/landing/article/:id" element={<LandingArticleDetailPage />} />
          <Route path="/landing/:pageName" element={<DynamicLandingPage />} />
          <Route path="/request-treatment" element={<RequestTreatmentPage />} />

          {/* Volunteer Registration Route - Requires Login */}
          <Route path="/vol" element={<VolunteerRegistrationPage />} />
          
          {/* Protected Admin Routes */}
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <AdminDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Doctor Routes */}
          <Route
            path="/dashboard/doctor"
            element={
              <ProtectedRoute allowedRoles={['coach', 'admin']}>
                <DashboardLayout>
                  <CoachDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Customer Routes */}
          <Route
            path="/dashboard/customer"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <DashboardLayout>
                  <CustomerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Manager Route - Single Route */}
          <Route
            path="/dashboard/manager"
            element={
              <ProtectedRoute allowedRoles={['manager']}>
                <DashboardLayout>
                  <ManagerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Volunteer Routes */}
          <Route
            path="/dashboard/volunteer"
            element={
              <ProtectedRoute allowedRoles={['volunteer']}>
                <DashboardLayout>
                  <VolunteerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Partner Routes */}
          <Route
            path="/dashboard/partner"
            element={
              <ProtectedRoute allowedRoles={['partner']}>
                <DashboardLayout>
                  <PartnerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Examiner Routes */}
          <Route
            path="/dashboard/examiner"
            element={
              <ProtectedRoute allowedRoles={['examiner']}>
                <DashboardLayout>
                  <ExaminerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Instructor Routes */}
          <Route
            path="/dashboard/instructor"
            element={
              <ProtectedRoute allowedRoles={['instructor']}>
                <DashboardLayout>
                  <InstructorDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Organizer Routes */}
          <Route
            path="/dashboard/organizer"
            element={
              <ProtectedRoute allowedRoles={['organizer']}>
                <DashboardLayout>
                  <OrganizerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Provider Routes */}
          <Route
            path="/dashboard/provider"
            element={
              <ProtectedRoute allowedRoles={['provider']}>
                <DashboardLayout>
                  <ProviderDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Coordinator Routes */}
          <Route
            path="/dashboard/coordinator"
            element={
              <ProtectedRoute allowedRoles={['coordinator']}>
                <DashboardLayout>
                  <CoordinatorDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Marketer Routes */}
          <Route
            path="/dashboard/marketer"
            element={
              <ProtectedRoute allowedRoles={['marketer']}>
                <DashboardLayout>
                  <MarketerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Audit Routes */}
          <Route
            path="/dashboard/audit"
            element={
              <ProtectedRoute allowedRoles={['audit']}>
                <DashboardLayout>
                  <AuditDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Customer Feature Routes */}
          <Route
            path="/dashboard/customer/habits"
            element={
              <ProtectedCustomerRoute requiredPermission="habitTracking">
                <DashboardLayout>
                  <HabitsPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/dashboard/customer/support"
            element={
              <ProtectedCustomerRoute requiredPermission="supportGroups">
                <DashboardLayout>
                  <SupportPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/dashboard/customer/coaching"
            element={
              <ProtectedCustomerRoute requiredPermission="coachingSessions">
                <DashboardLayout>
                  <CoachingPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/dashboard/customer/education"
            element={
              <ProtectedCustomerRoute requiredPermission="miniCourses">
                <DashboardLayout>
                  <EducationPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/dashboard/customer/points"
            element={
              <ProtectedCustomerRoute requiredPermission="pointsRewards">
                <DashboardLayout>
                  <PointsPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/dashboard/customer/trips"
            element={
              <ProtectedCustomerRoute requiredPermission="changeTrips">
                <DashboardLayout>
                  <TripsPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/dashboard/customer/treatment"
            element={
              <ProtectedCustomerRoute requiredPermission="treatmentAid">
                <DashboardLayout>
                  <TreatmentSupportPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/dashboard/customer/settings"
            element={
              <ProtectedCustomerRoute requiredPermission="settings">
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedCustomerRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['coach', 'admin', 'examiner', 'manager', 'partner', 'volunteer', 'audit']}>
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/landing" replace />} />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;